import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router";

import { useContractProfileLoadQuery } from "src/app/features/ContractProfile/services/server/ContractProfileApi";
import type { ContractProfileEntity } from "src/app/features/ContractProfile/types/ContractProfileEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { InheritedFieldChangeConfirmationModal } from "../../../../../components/Modal/InheritedFieldChangeConfirmationModal";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { useReturnToUrl } from "../../../../../core/router/navigation/returnTo";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showWarningNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { ID } from "../../../../../core/types/fe";
import { deepClone, isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";
import { useContractPhysicalSaveMutation } from "../../services/server/ContractApi";
import type { ContractEntity } from "../../types/ContractEntity";
import type { ContractAppProfileEntity } from "../../types/subTypes/ContractAppProfileEntity";

const CONTRACT_PROFILE_UPDATE_FIELDS = [
    "acquirerId",
    "appProfileListId",
    "receiptHeader",
    "merchantData",
    "receiptFooter",
    "messageForSuccessfulTransaction",
    "messageForFailedTransaction",
    "courtesyMessage",
    "dccServiceProviderForeignKeyId",
    "dccTemplate",
    "additionalServices",
];

export const ContractPhysicalCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("contract-physical");
    const [searchParams] = useSearchParams();
    const siteId = searchParams.get("siteId") ?? undefined;

    const [formData, setFormData] = useState<ContractEntity>({ siteId, physical: true } as ContractEntity);
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useContractPhysicalSaveMutation();
    const listPath = useReturnToUrl(`${BASE_PATH}/${ResourceName.CONTRACTS}`);
    const completeSubmit = useFormSubmitCompletion(formAction, listPath);
    const prevContractProfileIdRef = useRef<ID | undefined>(undefined);
    const pendingFormDataRef = useRef<ContractEntity | null>(null);
    const formDataRef = useRef(formData);
    const [contractProfileId, setContractProfileId] = useState<ID | undefined>(undefined);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const applyUpdatedData = useCallback((updatedData: ContractEntity) => {
        formDataRef.current = updatedData;

        if (updatedData.contractProfileId !== prevContractProfileIdRef.current) {
            setContractProfileId(updatedData.contractProfileId);
            setFormData(updatedData);

            if (updatedData.contractProfileId === undefined) {
                prevContractProfileIdRef.current = undefined;
            }
        }
    }, []);

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback(
        (newData: ContractEntity) => {
            const updatedData = {
                ...newData,
                appProfileListIdCheckReadOnly: formDataRef.current?.appProfileListIdCheckReadOnly,
                dccServiceProviderForeignKeyIdCheckReadOnly: formDataRef.current?.dccServiceProviderForeignKeyIdCheckReadOnly,
                dccTemplateCheckReadOnly: formDataRef.current?.dccTemplateCheckReadOnly,
            };

            const hasContractProfileChange = updatedData.contractProfileId !== prevContractProfileIdRef.current;
            if (hasContractProfileChange && updatedData.contractProfileId !== undefined) {
                pendingFormDataRef.current = updatedData;
                setIsConfirmModalOpen(true);
                setFormData({ ...formDataRef.current });
                return;
            }

            applyUpdatedData(updatedData);
        },
        [applyUpdatedData],
    );

    const onConfirmContractProfileChange = useCallback(() => {
        const pendingData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        if (pendingData) {
            applyUpdatedData(pendingData);
        }
    }, [applyUpdatedData]);

    const onCancelContractProfileChange = useCallback(() => {
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        setFormData({ ...formDataRef.current });
    }, []);

    // HANDLE WATCH FIELDS
    const { data: contractProfileEntity } = useContractProfileLoadQuery(
        { entityId: contractProfileId ? `${contractProfileId}` : undefined },
        { skip: !contractProfileId },
    );

    const updateWatchedFields = useCallback(() => {
        const currentContractProfileId = formDataRef.current?.contractProfileId;

        if (!currentContractProfileId || !contractProfileEntity || String(contractProfileEntity.id) !== String(currentContractProfileId))
            return null;

        prevContractProfileIdRef.current = currentContractProfileId;
        if (!formDataRef.current) return null;

        const newFormData = deepClone(formDataRef.current);
        const contractProfile = contractProfileEntity;

        // HANDLE UPDATE FIELDS
        // These fields inherit Contract Profile values
        if (contractProfile) {
            CONTRACT_PROFILE_UPDATE_FIELDS.forEach((fieldName: string) => {
                newFormData[fieldName] = contractProfile[fieldName as keyof ContractProfileEntity];
            });
        }

        // HANDLE VISIBILITY OF FIELDS FROM RO PROPERTY IN CONTRACT PROFILE
        // For each RO field name from Contract Profile object, the value is assigned to local state.
        // RO properties are used to control visibility of the fields in form.
        newFormData.appProfileListIdCheckReadOnly = contractProfile?.["applicationProfileListRO" as keyof ContractProfileEntity];
        newFormData.dccServiceProviderForeignKeyIdCheckReadOnly =
            contractProfile?.["dccServiceProviderForeignKeyRO" as keyof ContractProfileEntity];
        newFormData.dccTemplateCheckReadOnly = contractProfile?.["dccTemplateRO" as keyof ContractProfileEntity];

        return newFormData;
    }, [contractProfileEntity]);

    useEffect(() => {
        if (!contractProfileEntity || !contractProfileId || String(contractProfileEntity.id) !== String(contractProfileId)) {
            return;
        }

        const prevContractProfileId = prevContractProfileIdRef.current;

        if (contractProfileId !== prevContractProfileId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [contractProfileId, updateWatchedFields, contractProfileEntity]);

    // HANDLE ON SAVE
    const shouldSaveContractAppProfile = ({ acceptMode, routingId, merchantId }: ContractAppProfileEntity): boolean => {
        return !!acceptMode || (!acceptMode && !routingId && !merchantId);
    };

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            const currentFormData = formDataRef.current;
            if (!valid || !currentFormData) {
                return;
            }

            // a contractAppProfile (accepted card type) is only valid to send to the BE if it has acceptedMode defined, alternatively if it is empty it should be skipped
            if (currentFormData.acceptedCardProfiles?.some((item) => !shouldSaveContractAppProfile(item))) {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("contract.error.contractAppProfiles"),
                        autoCloseDisabled: true,
                    }),
                );
                return;
            }
            // Only include contractAppProfile if it has acceptedMode defined
            const updatedFormData = {
                ...currentFormData,
                acceptedCardProfiles: currentFormData.acceptedCardProfiles?.filter((item) => !!item.acceptMode),
            };

            const result = await saveFormData({ data: updatedFormData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", { id: result.data.id, resource: "Contract" }),
                    `${BASE_PATH}/${ResourceName.CONTRACTS}/${ResourceVariant.PHYSICAL}/${FORM_ACTION.EDIT}/${result.data.id}?discriminator=PHYSICAL&service=true&id=${result.data.id}`,
                );
            }
        },
        [saveFormData, completeSubmit, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<ContractEntity>
                    name="PhysicalContractCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
            <InheritedFieldChangeConfirmationModal
                open={isConfirmModalOpen}
                onConfirm={onConfirmContractProfileChange}
                onCancel={onCancelContractProfileChange}
            />
        </Spin>
    );
};
